export type AnnouncementStatus = 'draft' | 'pending' | 'approved' | 'rejected';
export type RelationshipType = 'parent' | 'grandparent' | 'sibling' | 'child' | 'spouse' | 'friend' | 'mentor' | 'colleague' | 'other' | 'crush';

export type SenderDisplayMode = 'anonymous' | 'named' | 'nickname' | 'confession';

export type { AnimationTheme } from './animationThemes';

export interface AnnouncementMetadata {
  senderDisplayMode?: SenderDisplayMode;
  senderNickname?: string;
  senderDisplayName?: string;
  isCrush?: boolean;
  chibiUrl?: string;
  /** Cinematic transition preset for the message → media handoff. */
  mediaTransition?: import('@/components/reveal/mediaTransitionPresets').MediaTransitionId;
}

// 6 emotional intent template types
export type TemplateType = 'apology' | 'gratitude' | 'hard_conversation' | 'love' | 'closure' | 'custom' | 'birthday' | 'anniversary' | 'celebration' | 'encouragement';

export type TypographyPreset = 'classic' | 'modern' | 'handwritten';
export type TextAlignment = 'center' | 'left' | 'mixed';
export type FloatingElementType = 'hearts' | 'suns' | 'stars' | 'smileys';
export type PhotoLayout = 'circular' | 'grid' | 'collage' | 'strip' | 'hero' | 'side_by_side_circles' | 'stacked_cards' | 'collage_grid' | 'tilted_scrapbook' | 'editorial_framed' | 'overlapping_layers' | 'top_banner' | 'floating_cluster';

export type LayoutFamily = 'magazine_cover' | 'editorial_story' | 'love_letter' | 'scrapbook' | 'minimal_modern' | 'polaroid_collage' | 'bold_poster' | 'soft_romantic' | 'memory_board' | 'elegant_classic';
export type HeadlineFont = 'elegant_serif' | 'modern_sans' | 'handwritten_script' | 'bold_editorial';
export type BodyFont = 'clean_serif' | 'clean_sans';
export type EmphasisStyle = 'italic_pullquote' | 'oversized_title' | 'quote_block' | 'highlighted_phrase' | 'none';
export type PhotoShape = 'rectangle' | 'rounded' | 'circle' | 'polaroid';
export type BackgroundStyle = 'solid' | 'gradient' | 'textured_paper' | 'dark_dramatic' | 'romantic_warm' | 'vignette';

export interface DecorationToggles {
  hearts: boolean;
  sparkles: boolean;
  lines: boolean;
  tape: boolean;
  florals: boolean;
  glow: boolean;
  confetti: boolean;
  flourishes: boolean;
}

export interface SectionToggles {
  title: boolean;
  fromLine: boolean;
  recipientName: boolean;
  body: boolean;
  pullQuote: boolean;
  signOff: boolean;
  date: boolean;
  subtitle: boolean;
  photoCaption: boolean;
  stickerRow: boolean;
}

export interface FloatingElement {
  type: FloatingElementType;
  color: string;
  enabled: boolean;
}

export interface CustomTemplateSettings {
  backgroundPhotoIndex: number | null;
  typographyPreset: TypographyPreset;
  titleBold: boolean;
  textAlignment: TextAlignment;
  autoContrast: boolean;
  fontColor: string;
  backgroundColor: string;
  floatingElements: FloatingElement[];
  photoLayout: PhotoLayout;
  // New custom editor fields
  layoutFamily: LayoutFamily;
  headlineFont: HeadlineFont;
  bodyFont: BodyFont;
  emphasisStyle: EmphasisStyle;
  photoShape: PhotoShape;
  backgroundStyle: BackgroundStyle;
  decorations: DecorationToggles;
  sections: SectionToggles;
}

export const DEFAULT_FLOATING_ELEMENTS: FloatingElement[] = [
  { type: 'hearts', color: '#ef4444', enabled: false },
  { type: 'suns', color: '#f59e0b', enabled: false },
  { type: 'stars', color: '#eab308', enabled: false },
  { type: 'smileys', color: '#f59e0b', enabled: false },
];

export const DEFAULT_DECORATIONS: DecorationToggles = {
  hearts: false, sparkles: false, lines: false, tape: false,
  florals: false, glow: false, confetti: false, flourishes: false,
};

export const DEFAULT_SECTIONS: SectionToggles = {
  title: true, fromLine: true, recipientName: true, body: true,
  pullQuote: false, signOff: true, date: false, subtitle: false,
  photoCaption: false, stickerRow: false,
};

export const DEFAULT_CUSTOM_SETTINGS: CustomTemplateSettings = {
  backgroundPhotoIndex: null,
  typographyPreset: 'classic',
  titleBold: true,
  textAlignment: 'center',
  autoContrast: true,
  fontColor: '#1a1a2e',
  backgroundColor: '#fdf6e3',
  floatingElements: DEFAULT_FLOATING_ELEMENTS,
  photoLayout: 'circular',
  layoutFamily: 'elegant_classic',
  headlineFont: 'elegant_serif',
  bodyFont: 'clean_serif',
  emphasisStyle: 'none',
  photoShape: 'rounded',
  backgroundStyle: 'solid',
  decorations: DEFAULT_DECORATIONS,
  sections: DEFAULT_SECTIONS,
};

export interface AnnouncementFormData {
  recipientName: string;
  relationship: RelationshipType | null;
  message: string;
  template: TemplateType;
  photoUrls: string[];
  photoFiles: File[];
  customSettings: CustomTemplateSettings;
  metadata?: AnnouncementMetadata;
  animationTheme: string;
}

export interface Announcement {
  id: string;
  user_id: string;
  recipient_name: string;
  relationship: RelationshipType;
  message: string;
  template: TemplateType;
  photo_urls: string[];
  status: AnnouncementStatus;
  public_slug: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  recipient_email?: string | null;
  sender_display_name?: string | null;
  is_anonymous?: boolean;
  courage_mode?: string;
  delivery_mode?: string;
}

export const RELATIONSHIP_OPTIONS: { value: RelationshipType; label: string; emoji: string }[] = [
  { value: 'parent', label: 'Parent', emoji: '👨‍👩‍👧' },
  { value: 'grandparent', label: 'Grandparent', emoji: '👴' },
  { value: 'sibling', label: 'Sibling', emoji: '👫' },
  { value: 'child', label: 'Child', emoji: '👶' },
  { value: 'spouse', label: 'Spouse/Partner', emoji: '💑' },
  { value: 'friend', label: 'Friend', emoji: '🤝' },
  { value: 'mentor', label: 'Mentor/Teacher', emoji: '🎓' },
  { value: 'colleague', label: 'Colleague', emoji: '💼' },
  { value: 'crush', label: 'Crush / Secret Admiration', emoji: '💌' },
  { value: 'other', label: 'Other', emoji: '❤️' },
];

// Re-export template options from new templates file
export { TEMPLATE_OPTIONS, getTemplateConfig } from './templates';
